import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Logo } from './Logo';
import { 
  ArrowLeft, 
  LogOut, 
  Save, 
  User, 
  Mail, 
  Phone, 
  MapPin,
  Calendar,
  Clock,
  FileText,
  Download,
  CheckCircle2,
  AlertCircle,
  Building
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusOptions = [
  { value: 'pending', label: 'Pending', color: '#FEF3C7', textColor: '#92400E' },
  { value: 'in_progress', label: 'In Progress', color: '#DBEAFE', textColor: '#1E40AF' },
  { value: 'completed', label: 'Completed', color: '#DCFCE7', textColor: '#166534' }
];

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'long',
      day: '2-digit', 
      month: 'long', 
      year: 'numeric' 
    });
  } catch {
    return dateStr;
  }
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', { 
    style: 'currency', 
    currency: 'GBP' 
  }).format(amount || 0);
};

export const AdminIntakeDetail = ({ intakeId, token, userRole, onBack, onLogout }) => {
  const [intake, setIntake] = useState(null);
  const [surveyors, setSurveyors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Editable fields
  const [status, setStatus] = useState('');
  const [selectedSurveyor, setSelectedSurveyor] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  const isAdmin = userRole === 'admin';

  useEffect(() => {
    fetchIntakeDetail();
    if (isAdmin) {
      fetchSurveyors();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intakeId]);

  const fetchIntakeDetail = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/intakes/${intakeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIntake(response.data);
      // Normalize status - treat 'paid' as 'pending'
      const normalizedStatus = response.data.status === 'paid' ? 'pending' : response.data.status;
      setStatus(normalizedStatus || 'pending');
      setSelectedSurveyor(response.data.assigned_surveyor || '');
    } catch (err) {
      toast.error('Failed to fetch submission details');
      if (err.response?.status === 401) {
        onLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchSurveyors = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/surveyors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSurveyors(response.data.surveyors || []);
    } catch (err) {
      console.error('Failed to fetch surveyors', err);
    }
  };

  const handleStatusChange = (newStatus) => {
    setStatus(newStatus);
    setHasChanges(true);
  };

  const handleSurveyorChange = (surveyorId) => {
    setSelectedSurveyor(surveyorId);
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Update status if changed
      const originalStatus = intake.status === 'paid' ? 'pending' : intake.status;
      if (status !== originalStatus) {
        await axios.put(
          `${API_URL}/api/admin/intakes/${intakeId}/status`,
          { status },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Status updated');
      }

      // Assign surveyor if changed (admin only)
      if (isAdmin && selectedSurveyor && selectedSurveyor !== intake.assigned_surveyor) {
        const response = await axios.put(
          `${API_URL}/api/admin/intakes/${intakeId}/surveyor`,
          { surveyor_id: selectedSurveyor },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.email_sent) {
          toast.success(`Surveyor assigned and notified via email`);
        } else {
          toast.success('Surveyor assigned');
        }
      }

      setHasChanges(false);
      // Refresh data
      fetchIntakeDetail();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-bg)' }}>
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: 'var(--color-navy)', borderTopColor: 'transparent' }}></div>
          <p style={{ color: 'var(--color-muted)' }}>Loading submission details...</p>
        </div>
      </div>
    );
  }

  if (!intake) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-bg)' }}>
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4" style={{ color: '#EF4444' }} />
          <p className="font-medium" style={{ color: 'var(--color-text)' }}>Submission not found</p>
          <button onClick={onBack} className="btn-primary mt-4">Back to Dashboard</button>
        </div>
      </div>
    );
  }

  const currentStatusOption = statusOptions.find(s => s.value === status) || statusOptions[0];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-bg)' }} data-testid="admin-intake-detail">
      {/* Header */}
      <header className="navbar">
        <div className="navbar-inner">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="flex items-center gap-2 text-sm" style={{ color: 'var(--color-muted)' }}>
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </button>
          </div>
          <div className="flex items-center gap-4">
            <Logo variant="dark" />
            <button onClick={onLogout} className="btn-secondary flex items-center gap-2">
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-5xl mx-auto">
          {/* Page Title */}
          <div className="flex items-start justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold font-mono" style={{ color: 'var(--color-navy)' }} data-testid="intake-reference">
                {intake.intake_reference_id}
              </h1>
              <p style={{ color: 'var(--color-muted)' }}>Submitted {formatDate(intake.date_submitted)}</p>
            </div>
            <div 
              className="px-4 py-2 rounded-full text-sm font-medium"
              style={{ backgroundColor: currentStatusOption.color, color: currentStatusOption.textColor }}
            >
              {currentStatusOption.label}
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Main Content - Left 2 columns */}
            <div className="lg:col-span-2 space-y-6">
              {/* Client Information */}
              <div className="card p-6">
                <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-navy)' }}>Client Information</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <User className="w-5 h-5 mt-0.5" style={{ color: 'var(--color-navy)' }} />
                    <div>
                      <p className="text-sm" style={{ color: 'var(--color-muted)' }}>Name</p>
                      <p className="font-medium" style={{ color: 'var(--color-text)' }}>{intake.client_name}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 mt-0.5" style={{ color: 'var(--color-navy)' }} />
                    <div>
                      <p className="text-sm" style={{ color: 'var(--color-muted)' }}>Email</p>
                      <a href={`mailto:${intake.client_email}`} className="font-medium hover:underline" style={{ color: 'var(--color-navy)' }}>
                        {intake.client_email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 mt-0.5" style={{ color: 'var(--color-navy)' }} />
                    <div>
                      <p className="text-sm" style={{ color: 'var(--color-muted)' }}>Phone</p>
                      <a href={`tel:${intake.client_phone}`} className="font-medium hover:underline" style={{ color: 'var(--color-navy)' }}>
                        {intake.client_phone || 'N/A'}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 mt-0.5" style={{ color: 'var(--color-navy)' }} />
                    <div>
                      <p className="text-sm" style={{ color: 'var(--color-muted)' }}>Site Address</p>
                      <p className="font-medium" style={{ color: 'var(--color-text)' }}>{intake.site_address || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {intake.is_listed_building && (
                  <div className="mt-4 p-3 rounded-lg flex items-center gap-2" style={{ backgroundColor: '#FEF3C7', border: '1px solid #FCD34D' }}>
                    <Building className="w-5 h-5" style={{ color: '#92400E' }} />
                    <span className="font-medium" style={{ color: '#92400E' }}>Listed Building - Internal Review Required</span>
                  </div>
                )}
              </div>

              {/* Project Details */}
              <div className="card p-6">
                <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-navy)' }}>Project Details</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm" style={{ color: 'var(--color-muted)' }}>Project Type</p>
                    <p className="font-medium" style={{ color: 'var(--color-text)' }}>
                      {intake.project_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: 'var(--color-muted)' }}>Turnaround</p>
                    <p className="font-medium" style={{ color: 'var(--color-text)' }}>{intake.turnaround_label || 'Standard'}</p>
                  </div>
                </div>

                {/* Quote Breakdown */}
                <div className="mt-6 pt-4" style={{ borderTop: '1px solid var(--color-border)' }}>
                  <p className="text-sm font-medium mb-3" style={{ color: 'var(--color-muted)' }}>Quote Breakdown</p>
                  <div className="space-y-2">
                    {intake.quote_breakdown?.map((item, idx) => (
                      <div key={`${item.label}-${idx}`} className="flex justify-between">
                        <span style={{ color: 'var(--color-text)' }}>{item.label}</span>
                        <span className="font-medium" style={{ color: 'var(--color-text)' }}>{formatCurrency(item.amount)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between pt-2 font-semibold" style={{ borderTop: '1px solid var(--color-border)' }}>
                      <span style={{ color: 'var(--color-navy)' }}>Total</span>
                      <span style={{ color: 'var(--color-navy)' }}>{formatCurrency(intake.quote_amount)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Uploaded Files */}
              <div className="card p-6">
                <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-navy)' }}>
                  Uploaded Files ({intake.files?.length || 0})
                </h2>
                {intake.files && intake.files.length > 0 ? (
                  <div className="space-y-2">
                    {intake.files.map((file, idx) => (
                      <div
                        key={file.filename ? `${file.filename}-${idx}` : idx}
                        className="flex items-center justify-between p-3 rounded-lg"
                        style={{ backgroundColor: 'var(--color-bg)', border: '1px solid var(--color-border)' }}
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5" style={{ color: 'var(--color-navy)' }} />
                          <div>
                            <p className="font-medium" style={{ color: 'var(--color-text)' }}>{file.filename}</p>
                            <p className="text-xs" style={{ color: 'var(--color-muted)' }}>
                              {file.size ? `${(file.size / 1024).toFixed(1)} KB` : 'Unknown size'}
                            </p>
                          </div>
                        </div>
                        {file.downloadUrl && (
                          <a 
                            href={file.downloadUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="btn-secondary flex items-center gap-2 text-sm"
                          >
                            <Download className="w-4 h-4" />
                            Download
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: 'var(--color-muted)' }}>No files uploaded</p>
                )}
              </div>
            </div>

            {/* Sidebar - Right column */}
            <div className="space-y-6">
              {/* Delivery Information */}
              <div className="card p-6">
                <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-navy)' }}>Delivery</h2>
                <div className="flex items-start gap-3 mb-4">
                  <Calendar className="w-5 h-5 mt-0.5" style={{ color: '#059669' }} />
                  <div>
                    <p className="text-sm" style={{ color: 'var(--color-muted)' }}>Estimated Delivery</p>
                    <p className="font-semibold" style={{ color: '#059669' }}>
                      {intake.estimated_delivery_date_formatted || 'N/A'}
                    </p>
                    {intake.delivery_note && (
                      <p className="text-xs mt-1" style={{ color: 'var(--color-muted)' }}>{intake.delivery_note}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Status and Assignment */}
              <div className="card p-6">
                <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-navy)' }}>Status and Assignment</h2>
                
                <div className="space-y-4">
                  {/* Status Dropdown */}
                  <div>
                    <label className="input-label block mb-2">Status</label>
                    <Select value={status} onValueChange={handleStatusChange}>
                      <SelectTrigger className="input-styled" data-testid="status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {statusOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Surveyor Dropdown - Admin only */}
                  {isAdmin && (
                    <div>
                      <label className="input-label block mb-2">Assign Surveyor</label>
                      <Select value={selectedSurveyor || "unassigned"} onValueChange={(val) => handleSurveyorChange(val === "unassigned" ? "" : val)}>
                        <SelectTrigger className="input-styled" data-testid="surveyor-select">
                          <SelectValue placeholder="Select surveyor..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">Unassigned</SelectItem>
                          {surveyors.map(surveyor => (
                            <SelectItem key={surveyor.id} value={surveyor.id}>
                              {surveyor.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {selectedSurveyor && (
                        <p className="text-xs mt-1" style={{ color: 'var(--color-muted)' }}>
                          {surveyors.find(s => s.id === selectedSurveyor)?.email}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Current Surveyor - Show for surveyors (read-only) */}
                  {!isAdmin && intake.assigned_surveyor_name && (
                    <div>
                      <label className="input-label block mb-2">Assigned To</label>
                      <p className="font-medium" style={{ color: 'var(--color-text)' }}>{intake.assigned_surveyor_name}</p>
                    </div>
                  )}

                  {/* Save Button */}
                  <button
                    onClick={handleSave}
                    disabled={!hasChanges || saving}
                    className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="save-changes-btn"
                  >
                    <Save className="w-4 h-4" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>

                  {hasChanges && (
                    <p className="text-xs text-center" style={{ color: '#F59E0B' }}>
                      You have unsaved changes
                    </p>
                  )}
                </div>
              </div>

              {/* Payment Info */}
              {intake.stripe_payment_intent && (
                <div className="card p-6">
                  <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-navy)' }}>Payment</h2>
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="w-5 h-5" style={{ color: '#059669' }} />
                    <span className="font-medium" style={{ color: '#059669' }}>Paid</span>
                  </div>
                  <p className="text-xs" style={{ color: 'var(--color-muted)' }}>
                    {intake.paid_at ? formatDate(intake.paid_at) : 'N/A'}
                  </p>
                  <p className="text-xs font-mono mt-2" style={{ color: 'var(--color-muted)' }}>
                    {intake.stripe_payment_intent}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};
